import { useEffect, useId, useState } from "react";
import { AlertTriangle } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  acceptWalletBetaDisclaimer,
  shouldShowWalletBetaDisclaimer,
} from "@/lib/wallet-beta-disclaimer";

export function WalletBetaDisclaimer({ userID }: { userID: string }) {
  const acknowledgementID = useId();
  const [open, setOpen] = useState(() =>
    shouldShowWalletBetaDisclaimer(userID),
  );
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    setAcknowledged(false);
    setOpen(shouldShowWalletBetaDisclaimer(userID));
  }, [userID]);

  const continueToWallet = () => {
    if (!acknowledged) return;
    acceptWalletBetaDisclaimer(userID);
    setOpen(false);
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="w-[calc(100vw-2rem)] max-w-[480px] gap-0 overflow-hidden rounded-[28px] border border-black/10 bg-[#f8f7f3] p-0 text-[#0e0c08] shadow-[0_28px_90px_rgba(0,0,0,0.28)]">
        <div className="border-b border-black/[0.07] px-6 pb-5 pt-6 sm:px-7 sm:pt-7">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-800">
              <AlertTriangle aria-hidden="true" className="h-5 w-5" />
            </span>
            <span className="rounded-full border border-amber-700/20 bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-900">
              Beta
            </span>
          </div>

          <AlertDialogHeader className="space-y-3 text-left">
            <AlertDialogTitle className="text-2xl font-bold tracking-[-0.025em]">
              Welcome to the GENRL beta
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px] leading-6 text-black/60">
              This is a beta app, so you may encounter issues or faults while using
              it. We are actively working on the app, which means features and the
              UI/UX may change as we improve the experience.
            </AlertDialogDescription>
            <p className="text-[14px] leading-6 text-black/60">
              Please reach out to the GENRL team if you have any issues or feedback.
            </p>
          </AlertDialogHeader>
        </div>

        <div className="space-y-5 px-6 py-5 sm:px-7 sm:py-6">
          <Label
            htmlFor={acknowledgementID}
            className="flex cursor-pointer items-start gap-3 rounded-2xl border border-black/[0.08] bg-white/70 p-4 text-left leading-5"
          >
            <Checkbox
              id={acknowledgementID}
              checked={acknowledged}
              onCheckedChange={(checked) => setAcknowledged(checked === true)}
              className="mt-0.5 h-5 w-5 rounded-md border-black/25 data-[state=checked]:border-[#0e0c08] data-[state=checked]:bg-[#0e0c08]"
            />
            <span className="text-[13px] font-medium text-black/70">
              I understand that this is a beta app and may contain issues or
              faults.
            </span>
          </Label>

          <AlertDialogFooter>
            <AlertDialogAction
              disabled={!acknowledged}
              onClick={continueToWallet}
              className="h-11 w-full rounded-xl bg-[#0e0c08] px-5 text-[13px] font-semibold text-white shadow-none hover:bg-[#25211b] disabled:cursor-not-allowed disabled:opacity-35"
            >
              Continue to wallet
            </AlertDialogAction>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
